import axios, { AxiosResponse } from 'axios';
import api from 'api/base';
import {
  GcashApplicationDocumentResponse,
  GcashCreateDocumentRequest,
  GcashCreatePageRequest,
  GcashDocumentPages,
  GcashDocumentsStaticticsResponse,
  GcashDocumentsStatusResponse,
  GcashDocumentTypesResponse,
  GcashExportDocumentRequest,
  GcashExportDocumentsRequest,
  GcashGetStorageSignedUrlResponse,
  GetPaginatedGcashApplicationDocumentsParams,
  GetPaginatedGcashApplicationDocumentsResponse,
  GetPaginatedGcashApplicationsParams,
  GetPaginatedGcashApplicationsResponse,
  PaginatedGcashActionLogsResponse,
  PaginatedGcashDocumentsByStatusResponse,
  PaginationGcashWithStatusRequest,
  GcashApplicationResponse,
} from 'features/gcash/api/types';
import { GcashDocumentStatuses } from 'features/gcash/enums/gcash-document-statuses.enum';

export const getGcashStatistics = async (): Promise<
  AxiosResponse<GcashDocumentsStaticticsResponse>
> => {
  return api.get<GcashDocumentsStaticticsResponse>(
    'portal/documents-statistics'
  );
};

export const getGcashLogs = async (params: {
  page: number;
  limit: number;
}): Promise<AxiosResponse<PaginatedGcashActionLogsResponse>> => {
  return api.get<PaginatedGcashActionLogsResponse>('portal/logs', {
    params,
  });
};

export const getGcashApplications = async (
  params: GetPaginatedGcashApplicationsParams
): Promise<AxiosResponse<GetPaginatedGcashApplicationsResponse>> => {
  return api.get<GetPaginatedGcashApplicationsResponse>('portal/applications', {
    params,
  });
};

export const createGcashApplication = async (data: {
  fullName: string;
}): Promise<AxiosResponse<GcashApplicationResponse>> => {
  return api.post<GcashApplicationResponse>('portal/applications/create', data);
};

export const getGcashApplication = (
  applicationId: string
): Promise<AxiosResponse<GcashApplicationResponse>> => {
  return api.get<GcashApplicationResponse>(
    `portal/applications/${applicationId}`
  );
};

export const getGcashApplicationDocuments = ({
  applicationId,
  ...params
}: GetPaginatedGcashApplicationDocumentsParams & {
  applicationId: string;
}): Promise<AxiosResponse<GetPaginatedGcashApplicationDocumentsResponse>> => {
  return api.get<GetPaginatedGcashApplicationDocumentsResponse>(
    `portal/applications/${applicationId}/documents`,
    { params }
  );
};

export const createGcashApplicationDocument = async ({
  applicationId,
  ...data
}: GcashCreateDocumentRequest & { applicationId: string }): Promise<
  AxiosResponse<GcashApplicationDocumentResponse>
> => {
  const fileName = (data.file as unknown as any).name;

  const { data: signedUrlData } =
    await api.get<GcashGetStorageSignedUrlResponse>(`portal/signed-url`, {
      params: {
        fileName,
      },
    });

  if (!signedUrlData?.signedUrl) {
    throw new Error('Failed to upload file');
  }

  await axios.put(signedUrlData.signedUrl, data.file, {
    headers: {
      'Content-Type': data.file.type,
    },
  });

  return api.post<GcashApplicationDocumentResponse>(
    `portal/applications/${applicationId}/documents/create`,
    {
      documentName: data.documentName,
      type: data.type,
      fileName: signedUrlData.fileName,
      originalFileName: fileName,
    }
  );
};

export const getGcashDocumentInfo = ({
  applicationId,
  documentId,
}: {
  applicationId: string;
  documentId: string;
}): Promise<AxiosResponse<GcashApplicationDocumentResponse>> => {
  return api.get<GcashApplicationDocumentResponse>(
    `portal/applications/${applicationId}/documents/${documentId}/info`
  );
};
export const exportGcashSingleDocument = ({
  applicationId,
  documentId,
  ...params
}: GcashExportDocumentRequest): Promise<AxiosResponse<Blob>> => {
  return api.get<Blob>(
    `portal/applications/${applicationId}/documents/${documentId}/export`,
    { params, responseType: 'blob' }
  );
};

export const createGcashNewPage = async ({
  file,
  fileName,
  applicationId,
  documentId,
}: GcashCreatePageRequest & {
  applicationId: string;
  documentId: string;
}): Promise<AxiosResponse<GcashDocumentPages>> => {
  const { data: signedUrlData } =
    await api.get<GcashGetStorageSignedUrlResponse>(`portal/signed-url`, {
      params: {
        fileName,
      },
    });

  if (!signedUrlData?.signedUrl) {
    throw new Error('Failed to upload file');
  }

  await axios.put(signedUrlData.signedUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });

  return api.post<GcashDocumentPages>(
    `portal/applications/${applicationId}/documents/${documentId}/pages/create`,
    { fileName: signedUrlData.fileName, originalFileName: fileName }
  );
};
export const changeGcashDocumentStatus = ({
  status,
  reason,
  applicationId,
  documentId,
}: {
  status: GcashDocumentStatuses;
  reason: string;
  applicationId: string;
  documentId: string;
}): Promise<AxiosResponse<GcashApplicationDocumentResponse>> => {
  return api.post<GcashApplicationDocumentResponse>(
    `portal/applications/${applicationId}/documents/${documentId}/change-status`,
    { status, reason }
  );
};

export const getGcashDocumentPages = ({
  applicationId,
  documentId,
}: {
  applicationId: string;
  documentId: string;
}): Promise<AxiosResponse<GcashDocumentPages>> => {
  return api.get<GcashDocumentPages>(
    `portal/applications/${applicationId}/documents/${documentId}/pages`
  );
};

export const getGcashDocumentsByStatus = (
  params: PaginationGcashWithStatusRequest
): Promise<AxiosResponse<PaginatedGcashDocumentsByStatusResponse>> => {
  return api.get<PaginatedGcashDocumentsByStatusResponse>(
    `portal/documents/pages`,
    {
      params,
      paramsSerializer: {
        indexes: null,
      },
    }
  );
};

export const exportGcashDocumentByStatus = (
  params: GcashExportDocumentsRequest
): Promise<AxiosResponse<Blob>> => {
  return api.get<Blob>(`portal/documents/export`, {
    params,
    // Match the list endpoint's serializer so multi-status arrays render as
    // repeated `status=A&status=B` query params rather than `status[]=...`,
    // which is what the portal-backend DTO parses.
    paramsSerializer: {
      indexes: null,
    },
    responseType: 'blob',
  });
};

export const exportGcashApplication = (
  applicationId: string
): Promise<AxiosResponse<Blob>> => {
  return api.get<Blob>(`portal/applications/${applicationId}/export`, {
    responseType: 'blob',
  });
};

export const getGcashDocumentsStatus = (
  applicationId: string
): Promise<AxiosResponse<GcashDocumentsStatusResponse>> => {
  return api.get<GcashDocumentsStatusResponse>(
    `portal/applications/${applicationId}/documents/status`
  );
};

export const getGcashDocumentTypes = (): Promise<
  AxiosResponse<GcashDocumentTypesResponse>
> => {
  return api.get<GcashDocumentTypesResponse>('portal/document-types');
};
